// :cow: Cosmose CONFIDENTIAL :iso:
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import { SpyLineChartDataSet } from '../../model/spy-line-chart-data-set';
import * as Chart from 'chart.js';
import { Tooltip } from '../../model/tooltip';
import { TooltipValue } from '../../model/tooltip-value';
import { SpyLineChartTooltipPositionService } from '@coa/app/shared/spy-line-chart-tooltip-position-service/spy-line-chart-tooltip-position.service';
import { ChartTooltipModel, ScaleTicks } from '@coa/app/shared/chart-js/chartjs-tooltip';
import { getBaseSpyLineChart } from '@coa/app/shared/chart-js/base-spy-line-chart';

@Component({
  selector: 'coa-spy-line-chart',
  templateUrl: './spy-line-chart.component.html',
  styleUrls: ['./spy-line-chart.component.scss'],
})
export class SpyLineChartComponent implements AfterViewInit, OnChanges {
  @Input() yAxisTicksConfig: ScaleTicks;
  @Input() label: string;
  @Input() dataset: SpyLineChartDataSet[];
  @ViewChild('chart', {read: ElementRef}) chartRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('tooltip', {read: ElementRef}) tooltip: ElementRef<HTMLDivElement>;
  isDatasetValid: boolean;
  chart: Chart;
  currentTooltip: Tooltip;
  private tooltips: Tooltip[];

  constructor(
    private cdr: ChangeDetectorRef,
    private tooltipPositionService: SpyLineChartTooltipPositionService,
  ) {
  }

  ngAfterViewInit(): void {
    this.drawChart();
  }

  ngOnChanges(): void {
    this.drawChart();
  }

  private getContext(): CanvasRenderingContext2D {
    return this.chartRef.nativeElement.getContext('2d');
  }

  private drawChart(): void {
    this.isDatasetValid = !!this.dataset && this.dataset.length > 0;

    if (this.chart) {
      this.chart.destroy();
    }
    this.cdr.detectChanges();

    if (!this.isDatasetValid) {
      return;
    }

    this.tooltips = this.extractTooltips();
    this.chart = new Chart(this.getContext(), this.getChartOptions());
  }

  private getChartOptions(): any {
    const spyLineChart = getBaseSpyLineChart();
    spyLineChart.data.labels = this.getLabels();
    spyLineChart.data.datasets = this.getChartjsDatasets();
    spyLineChart.options.tooltips.custom = ((tooltip): any => this.tooltipCallback(tooltip));
    if (this.yAxisTicksConfig) {
      spyLineChart.options['scales']['yAxes'][0]['ticks'] = {
        ...spyLineChart.options['scales']['yAxes'][0]['ticks'],
        ...this.yAxisTicksConfig,
      };
    }

    return spyLineChart;
  }

  private tooltipCallback(tooltip: ChartTooltipModel): void {
    if (!tooltip.body) {
      this.currentTooltip = null;
      return;
    }

    const index = tooltip.dataPoints[0].index;
    this.currentTooltip = this.tooltips[index];
    this.cdr.detectChanges();
    const position = this.tooltipPositionService.repositionTooltip(
      this.chart.canvas.getBoundingClientRect().width,
      this.tooltip.nativeElement.offsetWidth,
      tooltip.caretX,
      tooltip.caretY,
    );
    this.tooltip.nativeElement.style.setProperty('left', `${ position.left }px`);
    this.tooltip.nativeElement.style.setProperty('top', `${ position.top }px`);
  }

  private getLabels(): string[] {
    return this.dataset.slice().shift().series.map(s => s.label);
  }

  private getChartjsDatasets(): any {
    return this.dataset.map(d => {
      return {
        borderColor: d.color,
        borderWidth: 2,
        data: d.series.map(s => s.value),
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
        pointBackgroundColor: d.color,
        backgroundColor: 'transparent',
      };
    });
  }

  private extractTooltips(): Tooltip[] {
    const result: Tooltip[] = [];
    const elementCount = this.dataset.slice().shift().series.length;

    for (let i = 0; i < elementCount; i++) {
      const title = this.dataset[0].series[i].label;
      const values: TooltipValue[] = this.dataset.map(set => {
        return {
          value: set.series[i]?.value,
          label: set.name,
          color: set.color,
        };
      });

      const entry = {
        index: i,
        title,
        values,
      };
      result.push(entry);
    }

    return result;
  }
}
